import React, { useMemo, useState, useEffect } from "react";
import {
  Search,
  ArrowLeft,
  Wallet,
  Briefcase,
  Clock,
  TrendingUp,
  RefreshCw,
  FileText,
  Filter,
  LayoutList,
  DollarSign,
  ChevronRight,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowRight
} from "lucide-react";
import ReusableTable from "../components/Table/ReusableTable";
import Button from "../components/Button/Button";
import KpiCard from "../components/Dashboard/KpiCard";
import { post } from "../utils/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DistributorDashboard = () => {
  const [searchInput, setSearchInput] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("current");
  const [selectedDateRange, setSelectedDateRange] = useState({
    from_date: "",
    to_date: "",
    display: "",
  });

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [monthsList, setMonthsList] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [transactionStatuses, setTransactionStatuses] = useState({});

  const userData = JSON.parse(localStorage.getItem("user_details") || "{}");
  const distributorPan = userData.pan_number;
  const userId = userData.id;

  const handleBack = () => {
    setSelectedTransaction(null);
  };

  const now = new Date();
  const lastUpdated = `${now.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })} – ${now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;

  useEffect(() => {
    if (distributorPan) {
      fetchDashboardData(selectedMonth);
    }
  }, [transactionStatuses]);

  const getMonthDateRange = (monthOption) => {
    const now = new Date();
    let year, month;

    if (monthOption === "current") {
      year = now.getFullYear();
      month = now.getMonth();
    } else {
      [year, month] = monthOption.split("-").map(Number);
      month = month - 1;
    }
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      from_date: formatDate(firstDay),
      to_date: formatDate(lastDay),
      display:
        monthOption === "current"
          ? `${now.toLocaleString("default", { month: "long" })} ${now.getFullYear()}`
          : `${new Date(year, month).toLocaleString("default", { month: "long" })} ${year}`,
    };
  };

  const generateMonthsList = () => {
    const now = new Date();
    const months = [];
    months.push({
      value: "current",
      label: `${now.toLocaleString("default", { month: "long" })} ${now.getFullYear()}`,
    });
    for (let i = 1; i <= 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const label = `${date.toLocaleString("default", { month: "long" })} ${date.getFullYear()}`;
      months.push({ value, label });
    }
    return months;
  };

  const handleMarkAsNotCorrect = async (transactionRow) => {
    try {
      const payload = {
        transaction_details_id: transactionRow.id,
        transaction_id: transactionRow.transactionId,
        user_Id: userId,
      };
      const response = await post("/transaction/notcorrectTransactionDetails", payload);
      if (response.data.Success) {
        toast.error("Transaction marked as not correct.");
        setSelectedTransaction((prev) => {
          if (!prev) return prev;
          const updatedDetails = prev.transactionDetails.map((item) =>
            item.id === transactionRow.id ? { ...item, statusId: 5, isNotCorrect: true } : item
          );
          return { ...prev, transactionDetails: updatedDetails };
        });
        setTransactionStatuses((prev) => ({ ...prev, [transactionRow.id]: 5 }));
      } else {
        toast.error("Failed to mark transaction as not correct.");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while updating.");
    }
  };

  const isValidDate = (dateString) => {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  };

  const fetchDashboardData = async (monthOption) => {
    try {
      setLoading(true);
      setError(null);
      const dateRange = getMonthDateRange(monthOption);
      setSelectedDateRange(dateRange);
      const payload = {
        distributor_pan: distributorPan,
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
      };
      if (!isValidDate(payload.from_date) || !isValidDate(payload.to_date)) {
        setError("Invalid date range");
        setLoading(false);
        return;
      }
      const response = await post("/distributordashboard/fetchDistributorDashboardDetails", payload);
      if (response.data.Success) {
        const data = response.data.Data[0];
        setDashboardData(data);
        if (data.table_details && data.table_details.length > 0) {
          const transformedData = data.table_details.map((item) => ({
            processMonth: item.process_month || "N/A",
            productCode: item.product_code || "N/A",
            totalCommission: parseFloat(item.total_commission) || 0,
            gst: parseFloat(item.gst) || 0,
            tds: parseFloat(item.tds) || 0,
            netCommission: parseFloat(item.net_commission_payable) || 0,
            utrNumber: item.utr_number || "N/A",
            invoiceNumber: item.invoice_number || "N/A",
            action: "View Transaction",
            originalData: item,
          }));
          setTableData(transformedData);
        } else {
          setTableData([]);
        }
      } else {
        setError(response.data.Message || "Failed to fetch data");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionDetails = async (rowData) => {
    try {
      setTransactionLoading(true);
      const payload = {
        distributor_pan: distributorPan,
        from_date: selectedDateRange.from_date,
        to_date: selectedDateRange.to_date,
        process_month: rowData.processMonth,
      };
      const response = await post("/distributordashboard/viewTransactions", payload);
      if (response.data.Success) {
        const transformedData = response.data.Data.map((item) => ({
          id: item.id || "N/A",
          transactionId: item.transaction_id || "NA",
          transactionDate: item.transaction_date,
          processMonth: item.process_month || "N/A",
          clientName: item.client_name || "N/A",
          pan: item.pan_number || "N/A",
          folioNumber: item.folio_no || "N/A",
          distributorCodeARN: item.distributor_arn_code || "N/A",
          distributorPan: item.distributor_pan_number || "N/A",
          productCode: item.product_code || "N/A",
          capitalCommitment: parseFloat(item.capital_commitment) || 0,
          placementFeePercent: parseFloat(item.placement_fee_per) || 0,
          placementFeeAmount: parseFloat(item.placement_fee_amount) || 0,
          upfrontFeePercent: parseFloat(item.upfront_fee_per) || 0,
          upfrontFeeAmount: parseFloat(item.upfront_fee_amount) || 0,
          trailPercent: parseFloat(item.trail_per) || 0,
          totalTrailAmount: parseFloat(item.total_trail_amount) || 0,
          trailStartMonth: item.trail_start_month || "N/A",
          trailEndMonth: item.trail_end_month || "N/A",
          monthlyTrailAmount: parseFloat(item.monthly_trail_amount) || 0,
          totalCommission: parseFloat(item.total_commission) || 0,
          gst: parseFloat(item.gst) || 0,
          tds: parseFloat(item.tds) || 0,
          statusId: item.status_id || "N/A",
          netCommissionPayable: parseFloat(item.net_commission_payable) || 0,
          remarks: item.uploaded_remarks || item.check_remarks || "No remarks",
          invoiceNumber: item.invoice_number || "N/A",
        }));
        setSelectedTransaction({ ...rowData, transactionDetails: transformedData });
      } else {
        toast.error(response.data.Message || "Failed to fetch transaction details");
      }
    } catch (err) {
      console.error("Transaction API Error:", err);
      toast.error("An error occurred while fetching transaction details");
    } finally {
      setTransactionLoading(false);
    }
  };

  useEffect(() => {
    const months = generateMonthsList();
    setMonthsList(months);
    if (distributorPan) {
      fetchDashboardData(selectedMonth);
    } else {
      setError("Distributor PAN not found in user data");
      setLoading(false);
    }
  }, [distributorPan]);

  const handleMonthChange = (e) => {
    const monthOption = e.target.value;
    setSelectedMonth(monthOption);
    fetchDashboardData(monthOption);
  };

  const mainColumns = useMemo(
    () => [
      { Header: "Process Month", accessor: "processMonth", size: "150px" },
      { Header: "Product Code", accessor: "productCode", size: "130px" },
      {
        Header: "Total Commission",
        accessor: "totalCommission",
        size: "160px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      {
        Header: "GST",
        accessor: "gst",
        size: "100px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      {
        Header: "TDS",
        accessor: "tds",
        size: "100px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      {
        Header: "Net Commission Payable",
        accessor: "netCommission",
        size: "180px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      { Header: "UTR Number", accessor: "utrNumber", size: "160px" },
      { Header: "Invoice Number", accessor: "invoiceNumber", size: "160px" },
      {
        Header: "Action",
        accessor: "action",
        size: "160px",
        Cell: ({ row }) => (
        <button
          onClick={() => fetchTransactionDetails(row.original)}
          className="h-[32px] px-4 rounded-lg bg-white border border-gray-200 text-indigo-600 font-black text-[10px] uppercase hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all shadow-sm flex items-center gap-1.5 active:scale-95"
        >
          View Details <ChevronRight size={14} />
        </button>
        ),
      },
    ],
    []
  );

  const transactionColumns = useMemo(
    () => [
      {
        Header: "Transaction Date",
        accessor: "transactionDate",
        size: "150px",
      },
      {
        Header: "Process Month",
        accessor: "processMonth",
        size: "150px",
      },
      { Header: "Client Name", accessor: "clientName", size: "150px" },
      { Header: "PAN", accessor: "pan", size: "120px" },
      { Header: "Folio Number", accessor: "folioNumber", size: "130px" },
      {
        Header: "Distributor Code APRN",
        accessor: "distributorCodeARN",
        size: "180px",
      },
      { Header: "Distributor PAN", accessor: "distributorPan", size: "140px" },
      { Header: "Product Code", accessor: "productCode", size: "130px" },
      {
        Header: "Capital Commitment",
        accessor: "capitalCommitment",
        size: "160px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      {
        Header: "Placement Fee %",
        accessor: "placementFeePercent",
        size: "150px",
        Cell: ({ value }) => `${value}%`,
      },
      {
        Header: "Placement Fee Amount",
        accessor: "placementFeeAmount",
        size: "180px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      {
        Header: "Upfront Fee %",
        accessor: "upfrontFeePercent",
        size: "140px",
        Cell: ({ value }) => `${value}%`,
      },
      {
        Header: "Upfront Fee Amount",
        accessor: "upfrontFeeAmount",
        size: "160px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      {
        Header: "Trail %",
        accessor: "trailPercent",
        size: "100px",
        Cell: ({ value }) => `${value}%`,
      },
      {
        Header: "Total Trail Amount",
        accessor: "totalTrailAmount",
        size: "160px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      {
        Header: "Trail Start Month",
        accessor: "trailStartMonth",
        size: "150px",
      },
      { Header: "Trail End Month", accessor: "trailEndMonth", size: "150px" },
      {
        Header: "Monthly Trail Amount",
        accessor: "monthlyTrailAmount",
        size: "180px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      {
        Header: "Total Commission",
        accessor: "totalCommission",
        size: "150px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      {
        Header: "GST",
        accessor: "gst",
        size: "100px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      {
        Header: "TDS",
        accessor: "tds",
        size: "100px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      {
        Header: "Net Commission Payable",
        accessor: "netCommissionPayable",
        size: "180px",
        Cell: ({ value }) => `₹${value.toLocaleString()}`,
      },
      { Header: "Remarks", accessor: "remarks", size: "200px" },
      {
        Header: "Is Not Correct",
        accessor: "isNotCorrect",
        size: "150px",
        Cell: ({ row }) => {
          const rowData = row.original;

          const statusId = rowData.statusId;

          if (rowData.invoiceNumber && rowData.invoiceNumber !== "N/A") {
            return (
              <span className="text-green-600 font-medium">
                Invoice number is generated
              </span>
            );
          }

          // If status is already marked as not correct
          if (statusId === 5 || transactionStatuses[rowData.id] === 5) {
            return (
              <span className="text-red-600 font-medium">
                Not Correct Marked
              </span>
            );
          }

          // Otherwise show the checkbox
          return (
            <input
              type="checkbox"
              checked={false}
              onChange={() => handleMarkAsNotCorrect(rowData)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
          );
        },
      },
    ],
    [transactionStatuses]
  );

  const filteredData = tableData.filter((row) =>
    Object.values(row).join(" ").toLowerCase().includes(searchInput.toLowerCase())
  );

  if (loading && !dashboardData) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={48} className="animate-spin text-indigo-500" />
          <span className="font-black text-sm tracking-[0.2em] uppercase text-gray-400">Loading Dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-screen-xl mx-auto p-4 h-full min-h-screen bg-gray-50/30 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-sm border border-indigo-100 transition-transform hover:scale-105">
            <TrendingUp size={26} />
          </div>
          <div>
            <h1 className="text-xl font-black text-gray-900 tracking-tight leading-tight uppercase">Distributor Commission Summary</h1>
            <p className="text-gray-500 text-[11px] font-bold uppercase tracking-widest italic flex items-center gap-1">
              Revenue & Commission Hub <ArrowRight size={10} />
            </p>
          </div>
        </div>
        <div className="text-right hidden md:block">
          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Master Synchronized</p>
          <p className="text-[11px] font-black text-gray-900 italic">{lastUpdated}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Pending For Payment"
          value={`₹${parseFloat(dashboardData?.card_details?.Pending_For_Payment || 0).toLocaleString()}`}
          icon={<Clock size={20} />}
          iconBgColor="#FFF7ED"
          iconColor="#EA580C"
          trend={0}
        />
        <KpiCard
          title="Invoice To Be Uploaded"
          value={`₹${parseFloat(dashboardData?.card_details?.Invoice_To_Be_Uploaded || 0).toLocaleString()}`}
          icon={<FileText size={20} />}
          iconBgColor="#EEF2FF"
          iconColor="#4F46E5"
        />
        <KpiCard
          title="Disputed Invoice"
          value={`₹${parseFloat(dashboardData?.card_details?.Disputed_Invoice || 0).toLocaleString()}`}
          icon={<AlertCircle size={20} />}
          iconBgColor="#FEF2F2"
          iconColor="#EF4444"
        />
        <KpiCard
          title="Total Invoiced"
          value={`₹${parseFloat(dashboardData?.card_details?.Total_Amount_Of_Invoice_Raised || 0).toLocaleString()}`}
          icon={<Wallet size={20} />}
          iconBgColor="#ECFDF5"
          iconColor="#10B981"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden min-h-[500px]">
        {selectedTransaction ? (
          <div className="p-0 animate-in fade-in slide-in-from-right-4 duration-500">
            <div className="px-6 py-5 bg-gray-50/30 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleBack}
                  className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:border-indigo-600 hover:shadow-lg hover:shadow-indigo-100 transition-all active:scale-90 shadow-sm transform hover:-translate-x-0.5"
                  title="Go Back"
                >
                  <ArrowLeft size={18} />
                </button>
                <div>
                  <h2 className="text-lg font-black text-gray-900 uppercase">Transaction Audit</h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 uppercase">{selectedTransaction.processMonth}</span>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{selectedTransaction.productCode}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-[10px] font-black text-gray-400 uppercase">Settlement Value</p>
                  <p className="text-lg font-black text-indigo-600 tracking-tight">₹{selectedTransaction.netCommission.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              {transactionLoading ? (
                <div className="flex flex-col items-center justify-center py-32 gap-3 text-gray-300">
                  <Loader2 size={40} className="animate-spin text-indigo-500" />
                  <span className="text-xs font-black uppercase tracking-widest">Decrypting Items...</span>
                </div>
              ) : (
                <ReusableTable
                  columns={transactionColumns}
                  data={selectedTransaction.transactionDetails}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="animate-in fade-in slide-in-from-left-4 duration-500">
            <div className="px-6 py-6 border-b border-gray-100 space-y-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Archive Period</label>
                  <div className="relative group">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-indigo-500 transition-colors" />
                    <select
                      value={selectedMonth}
                      onChange={handleMonthChange}
                      className="w-full lg:w-64 pl-10 pr-4 h-[44px] bg-gray-50/50 border border-gray-200 rounded-xl text-sm font-bold text-gray-700 outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 appearance-none transition-all cursor-pointer"
                    >
                      {monthsList.map((month) => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex-1 flex flex-col gap-1.5 max-w-md">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Search Registry</label>
                  <div className="relative group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-indigo-500 transition-colors" />
                    <input
                      type="text"
                      placeholder="Search by Product, UTR, or Amount..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="w-full pl-10 pr-4 h-[44px] bg-gray-50/50 border border-gray-200 rounded-xl text-sm font-bold text-gray-700 outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all placeholder:text-gray-300"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="p-1">
              {filteredData.length > 0 ? (
                <ReusableTable columns={mainColumns} data={filteredData} />
              ) : (
                <div className="flex flex-col items-center justify-center py-40 text-gray-300 gap-4">
                  <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center border border-dashed border-gray-200">
                    <LayoutList size={32} className="text-gray-200" />
                  </div>
                  <div className="text-center">
                    <p className="font-black text-sm uppercase tracking-widest text-gray-400">Registry Empty</p>
                    <p className="text-[11px] font-medium italic mt-1">No transaction data found for this period.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />
    </div>
  );
};

export default DistributorDashboard;
